import { Cifra } from '../../componentes/Cifra';
import { Icono } from '../../componentes/Icono';
import { Ventana } from '../../componentes/Ventana';
import type { Impacto as DatosImpacto } from './cambios';
import { lineaAfectado, queDespues, textoCifra, type Palabras } from './textos';

/**
 * 3.4 «Revisar el impacto y confirmar» (RV Ventana): cuántas reservas o citas deja fuera el cambio (`affected_count`,
 * la única cifra, con RV Cifra), qué cambio es, la lista con su fecha y hora local y el texto que dio su módulo
 * (`holder_label`, G253; nunca datos del cliente), qué pasa después (G254) y «Calculado ahora · vale 15 minutos».
 * Caducada: la lista atenuada, el aviso rojo y «Calcular otra vez». «Confirmar el cambio» no tiene «Deshacer» (G159);
 * sin `disponibilidad.cambios.confirmar` no se puede confirmar (G259).
 */
export function Impacto({ impacto, palabras, anio, movil, puedeConfirmar, onConfirmar, onCerrar }: { impacto: DatosImpacto | null; palabras: Palabras; anio: number; movil: boolean; puedeConfirmar: boolean; onConfirmar: () => void; onCerrar: () => void }) {
    const i = impacto;
    const n = i?.vista.affected_count ?? 0;
    const caducada = i?.caducada === true;
    const quien = palabras.citas ? 'citas' : 'reservas';
    const texto = i?.cambio.zona === true ? 'Las reservas guardan su hora. Con otra zona, esa hora sería otra para el local.' : `Antes de guardar, mira qué ${quien} deja fuera este cambio.`;
    return (
        <Ventana
            abierta={i !== null}
            titulo="Revisar el impacto y confirmar"
            texto={texto}
            accion={!puedeConfirmar && !caducada ? '' : caducada ? 'Calcular otra vez' : 'Confirmar el cambio'}
            accionIcono={caducada ? 'refresh-cw' : 'check'}
            cancelar="Volver"
            cancelarIcono="arrow-left"
            ocupado={i?.ocupado === true}
            ocupadoTexto={caducada ? 'Calculando…' : 'Guardando…'}
            ancho="560px"
            movil={movil}
            onAccion={onConfirmar}
            onCerrar={onCerrar}
        >
            {i !== null && (
                <div className="rv-imp">
                    <div className="rv-imp__cifra">
                        <Cifra valor={n} tamano={28} peso={800} />
                        <span>{textoCifra(n, palabras)}</span>
                    </div>
                    <p className="rv-imp__que">
                        <Icono nombre={i.cambio.icono} />
                        <span>{i.cambio.contexto}</span>
                    </p>
                    {caducada && (
                        <p role="alert" className="rv-imp__caducada">
                            <Icono nombre="clock" />
                            <span>Han pasado más de 15 minutos o el horario ha cambiado mientras tanto. Calcula otra vez antes de confirmar.</span>
                        </p>
                    )}
                    <div role="list" aria-label="Reservas y citas afectadas" className={`rv-imp__lista${caducada ? ' rv-imp__lista--apagada' : ''}`}>
                        {i.vista.affected_holds.map((a) => {
                            const l = lineaAfectado(a, palabras, anio);
                            return (
                                <div key={a.hold_id} role="listitem" className="rv-imp__fila">
                                    <span className="rv-imp__baldosa">
                                        <Icono nombre={l.icono} />
                                    </span>
                                    <span className="rv-imp__texto">
                                        <strong>{l.cuando}</strong>
                                        <span>{l.que}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="rv-imp__despues">
                        {queDespues(i.cambio.todoElNegocio, palabras).map((m) => (
                            <p key={m.titulo}>
                                <Icono nombre={m.icono} />
                                <span>
                                    <strong>{m.titulo}</strong>
                                    <span>{m.texto}</span>
                                </span>
                            </p>
                        ))}
                    </div>
                    {!puedeConfirmar && <p className="rv-imp__pie">Lo confirman el propietario y los administradores.</p>}
                    <p className="rv-imp__pie">
                        <Icono nombre="clock" />
                        <span>{caducada ? 'El cálculo ha caducado.' : 'Calculado ahora · vale 15 minutos.'}</span>
                    </p>
                </div>
            )}
        </Ventana>
    );
}
